import cn from 'classnames';
import { isServer } from 'core-lib/utils/utils';
import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { AppBar, AppBarProps } from 'ui-v2/components/AppBar';
import BackButton from 'ui-v2/components/BackButton';
import { getRandomNum } from '../../utils';
import { AnimationTypes, TimeoutMapping, Transition } from '../Transitions/Transition/Transition';
import s from './Modal.module.scss';
import { onModalVisibilityChange } from './Modal.utils';
import Icon from '../Icon/Icon';

export type ModalTypeTypes =
  | 'bottomSheet'
  | 'fullScreenBottomSheet'
  | 'popup'
  | 'page'
  | 'fullScreenPopup';

export type ModalHeadTypes = {
  show: boolean;
  title?: string;
  subtitle?: string;
  onClose?: () => void;
  appBarData?: AppBarProps;
};

export type ModalTypes = {
  head?: ModalHeadTypes;
  variant?: ModalTypeTypes;
  closeOnClickOutside?: boolean;
  blurredBg?: boolean;
  show?: boolean;
  animation?: AnimationTypes;
  noPadding?: boolean;
  customeBodyStyle?: any;
  customeMainStyle?: any;
  rootStyle?: any;
  refValue?: number;
  isWeb?: boolean;
  direction?: 'right' | 'left';
};

export function Modal({
  head = {
    show: true,
  },
  refValue = getRandomNum(),
  variant = 'bottomSheet',
  children,
  closeOnClickOutside = true,
  blurredBg = false,
  show = false,
  animation,
  noPadding = false,
  customeBodyStyle = {},
  customeMainStyle = {},
  rootStyle,
  isWeb = false,
  direction,
}: React.PropsWithChildren<ModalTypes>) {
  if (isServer()) return null;
  const compRef = React.useRef(refValue);
  const isShownOnce = React.useRef(false);
  const [showComp, setShowComp] = React.useState(false);

  const mainAnim: AnimationTypes = React.useMemo(() => {
    let mainAnim: AnimationTypes = animation;
    if (variant !== 'page') {
      mainAnim = {
        type: 'fade',
      };
    }
    return mainAnim;
  }, [variant]);

  const setType: ModalTypeTypes = React.useMemo(() => {
    let setType: ModalTypeTypes = variant;
    if (!isServer()) {
      if (variant === 'bottomSheet' && global.window.innerWidth > 767) {
        setType = 'popup';
      }
      if (variant === 'fullScreenBottomSheet' && global.window.innerWidth > 767) {
        setType = 'fullScreenPopup';
      }
    }
    return setType;
  }, [variant]);

  const bodyAnim: AnimationTypes = React.useMemo(() => {
    let bodyAnim: AnimationTypes;
    if (animation) {
      bodyAnim = animation;
    } else {
      switch (setType) {
        case 'bottomSheet':
          bodyAnim = {
            type: 'btt',
            timeout: animation?.timeout ?? 'm',
          };
          break;
        case 'fullScreenBottomSheet':
          bodyAnim = {
            type: 'btt',
            timeout: animation?.timeout ?? 'm',
          };
          break;
        case 'popup':
          bodyAnim = {
            type: 'popup',
            timeout: animation?.timeout ?? 'm',
          };
          break;
        case 'fullScreenPopup':
          bodyAnim = {
            type: 'popup',
            timeout: animation?.timeout ?? 'm',
          };
          break;
        case 'page':
          bodyAnim = {
            type: 'fade',
            timeout: animation?.timeout ?? 'm',
          };
          break;
        default:
      }
    }
    return bodyAnim;
  }, [setType]);

  const [showContent, setShowContent] = React.useState(false);

  useEffect(() => {
    if (!show || typeof document === 'undefined') return undefined;
    const { body } = document;
    if (!body) return undefined;
    body.dataset.openModalCount = String(Number(body.dataset.openModalCount || '0') + 1);
    return () => {
      const next = Number(body.dataset.openModalCount || '1') - 1;
      if (next > 0) body.dataset.openModalCount = String(next);
      else delete body.dataset.openModalCount;
    };
  }, [show]);

  React.useEffect(() => {
    setShowComp(show);
    if (show) {
      if (!isShownOnce.current) isShownOnce.current = true;
      if (bodyAnim) {
        const timeout = setTimeout(() => {
          setShowContent(true);
          clearTimeout(timeout);
        }, 50);
      } else setShowContent(true);
    } else if (isShownOnce.current) {
      if (!bodyAnim) {
        const timeout = setTimeout(
          () => {
            setShowContent(false);
            clearTimeout(timeout);
          },
          TimeoutMapping[animation?.timeout ?? 'm'],
        );
      } else {
        setShowContent(false);
      }
    }

    if (isShownOnce.current) onModalVisibilityChange(show, compRef.current);

    return () => {
      onModalVisibilityChange(false, compRef.current);
    };
  }, [show]);

  function close() {
    if (typeof head?.onClose === 'function') {
      head?.onClose();
    }
  }

  function _renderAppBar() {
    let appBarProps: AppBarProps = {
      bgColor: 'primary',
    };

    appBarProps.withBottomLine = false;
    appBarProps.backButton = {
      show: false,
    };
    if (variant === 'bottomSheet') {
      appBarProps.title = {
        label: head.title,
      };
      appBarProps.subtitle = {
        label: head.subtitle,
      };
      appBarProps.actions = [
        {
          onClick: close,
          children: <Icon name="CrossCircleBold" color="quaternary" width={32} height={32} />,
        },
      ];
    }
    if (variant === 'fullScreenBottomSheet') {
      appBarProps.actions = [
        {
          onClick: close,
          children: <Icon name="CrossCircleBold" color="quaternary" width={32} height={32} />,
        },
      ];
      appBarProps.title = {
        label: head.title,
      };
      appBarProps.subtitle = {
        label: head.subtitle,
      };
    }
    if (variant === 'page') {
      appBarProps.actions = [];
      appBarProps.title = {
        label: head.title,
      };
      appBarProps.subtitle = {
        label: head.subtitle,
      };
      appBarProps.withBottomLine = true;
      appBarProps.backButton = {
        show: true,
        onClick: close,
      };
    }

    if (head?.appBarData) appBarProps = { ...appBarProps, ...head.appBarData };

    if (variant === 'bottomSheet') {
      appBarProps.withSafeArea = false;
    }
    return <AppBar {...appBarProps} />;
  }

  const appRoot = global.document.getElementsByTagName('BODY')[0];
  
  return ReactDOM.createPortal(
    <Transition show={showComp} animation={mainAnim}>
      <div
        id={`modalId-${compRef.current}`}
        className={cn(s.root, {
          [s.bottomSheet]: setType === 'bottomSheet',
          [s.fullScreenBottomSheet]: setType === 'fullScreenBottomSheet',
          [s.popup]: setType === 'popup',
          [s.fullScreenPopup]: setType === 'fullScreenPopup',
          [s.page]: setType === 'page',
          [s.blurredBg]: blurredBg,
          [s.isWeb]: !isWeb,
          [s.right]: direction === 'right',
          [s.left]: direction === 'left',
        })}
        onClick={e => {
          if (closeOnClickOutside) {
            close();
          } else {
            e.stopPropagation();
          }
        }}
        style={rootStyle}
      >
        <Transition show={showContent} animation={bodyAnim}>
          <div
            className={cn(s.main)}
            onClick={e => {
              e.stopPropagation();
            }}
            style={customeMainStyle}
          >
            <BackButton type="hidden" onBack={close} />
            {head.show && setType !== 'popup' && setType !== 'fullScreenPopup' && (
              <div className={s.head}>{_renderAppBar()}</div>
            )}
            <div
              className={cn(s.body, {
                [s.noPadding]: noPadding,
              })}
              style={customeBodyStyle}
            >
              {(setType === 'popup' || setType === 'fullScreenPopup') && head?.show && (
                <div className={s.floatingCross}>
                  <BackButton noPadding onBack={() => close()}>
                    <Icon name="CrossCircleBold" color="quaternary" width={32} height={32} />
                  </BackButton>
                </div>
              )}
              {children}
            </div>
          </div>
        </Transition>
      </div>
    </Transition>,
    appRoot,
  );
}
